import type { TreeItemType, IconNames } from '@workramp/ui';
import { Icon, Button, Stack, moveNodes, Tree, Utility, useDialog } from '@workramp/ui';
import { ConfirmModal } from '~/features/common/components/ConfirmModal';
import {
  MIXPANEL_COMMUNITIES_MENU_ITEM_ADDED,
  MIXPANEL_COMMUNITIES_MENU_ITEM_DELETED,
} from '~/clc/features/community/constants';
import { MixPanel } from '~/common';
import { APP_CONSTANTS, getCurrentUser } from '~/common/explicit-exports';
import { MenuLinkModal } from './MenuLinkModal';

export type MenuLink = {
  label: string;
  href: string;
  openInNewTab?: boolean;
  iconName?: IconNames;
};

export type MenuLinkModalModes = 'edit' | 'create';

interface MenuSettingProps {
  label: string;
  value?: MenuLink[];
  onChange?: (links: MenuLink[]) => void;
  mixpanelInfo: { groupType: string };
  externalBaseUrl?: string;
  activeProducts: (typeof APP_CONSTANTS.CUSTOMER_LEARNING_CLOUD_HUB.PRODUCT_TYPES)[keyof typeof APP_CONSTANTS.CUSTOMER_LEARNING_CLOUD_HUB.PRODUCT_TYPES][];
}

const TreeIcon = (iconName: IconNames) => <Icon name={iconName} aria-hidden />;

export const MenuSetting = ({
  label,
  value: links = [],
  onChange,
  mixpanelInfo,
  externalBaseUrl = '',
  /**
   * List of products in the hub that have records and not just enabled in spacemountain
   */
  activeProducts = [],
}: MenuSettingProps) => {
  const menuLinkModal = useDialog(MenuLinkModal);
  const confirmDeleteModal = useDialog(ConfirmModal);
  const activeFeatures = getCurrentUser()?.enterprise.active_features;
  const isCommunityFeatureEnabled =
    activeFeatures?.includes(APP_CONSTANTS.PRODUCT_FEATURES.COMMUNITIES) &&
    activeProducts?.includes(APP_CONSTANTS.CUSTOMER_LEARNING_CLOUD_HUB.PRODUCT_TYPES.COMMUNITY);
  const isAcademyFeatureEnabled = activeProducts?.includes(
    APP_CONSTANTS.CUSTOMER_LEARNING_CLOUD_HUB.PRODUCT_TYPES.ACADEMY,
  );

  const PREDEFINED_LINKS: MenuLink[] = [
    {
      label: 'Home',
      href: '/',
      iconName: 'home:regular',
    },
    ...(isAcademyFeatureEnabled
      ? [
          {
            label: 'Academy',
            href: '/academy',
            iconName: 'academy:regular',
          } as MenuLink,
        ]
      : []),
    ...(isCommunityFeatureEnabled
      ? [
          {
            label: 'Community',
            href: '/community',
            iconName: 'community:regular',
          } as MenuLink,
        ]
      : []),
    ...(isAcademyFeatureEnabled
      ? [
          {
            label: 'Catalog',
            href: '/catalog',
            iconName: 'content:regular',
          } as MenuLink,
        ]
      : []),
  ];

  const getIconName = (href: string) => PREDEFINED_LINKS.find((link) => link.href === href)?.iconName;

  const getTreeItems = () =>
    links?.map<TreeItemType>((link, index) => ({
      id: link.label,
      name: link.label,
      icon: () => TreeIcon(getIconName(link.href) || 'link:regular'),
      itemActions: [
        {
          label: 'Delete',
          iconName: 'trash:regular',
          onAction: () => {
            confirmDeleteModal.open({
              title: 'Are you sure you want to delete this menu item?',
              body: '',
              onConfirm: () => handleDeleteMenuItem(index),
              onCancel: confirmDeleteModal.remove,
            });
          },
        },
        {
          label: 'Edit',
          iconName: 'edit:regular',
          onAction: () => {
            handleOpenModal(link, index);
          },
        },
      ],
    }));

  const handleReorder = (itemIds: Set<string | number>, parentId: string | number | null, index: number) => {
    const treeData = moveNodes(getTreeItems(), itemIds, parentId, index);
    const reorderedLinks = links.sort((a, b) => {
      const aIndex = treeData.findIndex((item) => item.id === a.label);
      const bIndex = treeData.findIndex((item) => item.id === b.label);
      return aIndex - bIndex;
    });
    onChange(reorderedLinks);
  };

  const handleEdit = (editedLink: MenuLink, editedLinkIndex) => {
    const newLinks = [...links];
    newLinks[editedLinkIndex] = editedLink;
    onChange(newLinks);
    menuLinkModal.remove();
  };

  const handleCreate = (newLink: MenuLink) => {
    if (mixpanelInfo) {
      MixPanel.track(`${MIXPANEL_COMMUNITIES_MENU_ITEM_ADDED}_${mixpanelInfo.groupType}`);
    }
    onChange([...links, newLink]);
    menuLinkModal.remove();
  };

  const handleDeleteMenuItem = (index) => {
    if (mixpanelInfo) {
      MixPanel.track(`${MIXPANEL_COMMUNITIES_MENU_ITEM_DELETED}_${mixpanelInfo.groupType}`);
    }
    onChange(links.filter((_, i) => i !== index));
    confirmDeleteModal.remove();
  };

  const handleOpenModal = (linkToEdit = null, linkIndexToEdit = null) => {
    menuLinkModal.open({
      externalBaseUrl,
      linkToEdit,
      predefinedLinks: PREDEFINED_LINKS,
      onEdit: (menuLink) => handleEdit(menuLink, linkIndexToEdit),
      onCreate: (menuLink) => handleCreate(menuLink),
      onClose: menuLinkModal.remove,
    });
  };

  return (
    <Stack size={2}>
      <Utility level="md">{label}</Utility>
      <Tree allowsDragAndDrop data={getTreeItems()} onMoveItems={handleReorder} selectionMode="single" />
      <div>
        <Button variant="text" leadingIconName="add:regular" onPress={() => handleOpenModal()}>
          Add menu item
        </Button>
      </div>
    </Stack>
  );
};
